import { EGiftCardStatus } from '@xeppt/xeppt-sdk/types';
import { EScheduleFrequency, EScheduleStatus } from '@xeppt/xeppt-sdk/types/schedule';
import moment from 'moment';

export function getNumberOfPayments({
    nextPaymentDate,
    lastPaymentDate,
    frequency
}: {
    nextPaymentDate: string;
    lastPaymentDate: string;
    frequency: EScheduleFrequency;
}) {
    // Parse dates and normalize to start of day to ignore timezone issues
    const startDate = moment(nextPaymentDate).startOf('day');
    const endDate = moment(lastPaymentDate).startOf('day');

    switch (frequency) {
        case EScheduleFrequency.ONCE:
            return 1; // Only a single payment is made

        case EScheduleFrequency.DAILY:
            return endDate.diff(startDate, 'days') + 1;

        case EScheduleFrequency.WEEKLY:
            return endDate.diff(startDate, 'weeks') + 1;

        case EScheduleFrequency.BIWEEKLY:
            return Math.floor(endDate.diff(startDate, 'days') / 14) + 1;

        case EScheduleFrequency.MONTHLY:
            return endDate.diff(startDate, 'months') + 1;

        case EScheduleFrequency.QUARTERLY:
            return Math.floor(endDate.diff(startDate, 'months') / 3) + 1;

        case EScheduleFrequency.SEMIANNUALLY:
            return Math.floor(endDate.diff(startDate, 'months') / 6) + 1;

        case EScheduleFrequency.ANNUALLY:
            return endDate.diff(startDate, 'years') + 1;

        default:
            throw new Error('Invalid payment frequency');
    }
}

export function getLastPaymentDate({
    nextPaymentAt,
    lastPaymentAt,
    number_of_payments,
    donePayments,
    frequency
}: {
    nextPaymentAt?: string | Date;
    lastPaymentAt?: string | Date;
    number_of_payments?: number;
    donePayments?: number;
    frequency: EScheduleFrequency;
}): string {
    // Determine which date to use as base
    const baseDate = nextPaymentAt || lastPaymentAt;

    if (!baseDate) {
        throw new Error('Either nextPaymentAt or lastPaymentAt must be provided');
    }

    const referenceDate = new Date(baseDate);

    // Validate the input date
    if (isNaN(referenceDate.getTime())) {
        throw new Error('Invalid date provided');
    }

    // Create a new date object to avoid mutating the original
    const finalPaymentDate = new Date(referenceDate);

    // Calculate how many payments to add
    let paymentsToAdd = 0;

    if (nextPaymentAt) {
        // If using nextPaymentAt, we add (remaining_payments - 1) because next payment is payment #1
        paymentsToAdd = number_of_payments ? number_of_payments - 1 : 0;
    } else {
        // If using lastPaymentAt, we add remaining_payments because last payment already happened
        paymentsToAdd = number_of_payments || 0;
    }

    // Handle the case where there are no more payments
    if (paymentsToAdd <= 0) {
        return finalPaymentDate.toISOString();
    }

    switch (frequency) {
        case EScheduleFrequency.ONCE:
            // If there is only one payment, return the base date
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.DAILY:
            finalPaymentDate.setDate(finalPaymentDate.getDate() + paymentsToAdd);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.WEEKLY:
            finalPaymentDate.setDate(finalPaymentDate.getDate() + paymentsToAdd * 7);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.BIWEEKLY:
            finalPaymentDate.setDate(finalPaymentDate.getDate() + paymentsToAdd * 14);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.MONTHLY:
            finalPaymentDate.setMonth(finalPaymentDate.getMonth() + paymentsToAdd);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.QUARTERLY:
            finalPaymentDate.setMonth(finalPaymentDate.getMonth() + paymentsToAdd * 3);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.SEMIANNUALLY:
            finalPaymentDate.setMonth(finalPaymentDate.getMonth() + paymentsToAdd * 6);
            return finalPaymentDate.toISOString();

        case EScheduleFrequency.ANNUALLY:
            finalPaymentDate.setFullYear(finalPaymentDate.getFullYear() + paymentsToAdd);
            return finalPaymentDate.toISOString();

        default:
            throw new Error('Invalid payment frequency');
    }
}

export const getFrequencyList = (t: (val: string) => string) => [
    { label: t('once'), value: EScheduleFrequency.ONCE },
    { label: t('daily'), value: EScheduleFrequency.DAILY },
    { label: t('weekly'), value: EScheduleFrequency.WEEKLY },
    { label: t('biweekly'), value: EScheduleFrequency.BIWEEKLY },
    { label: t('monthly'), value: EScheduleFrequency.MONTHLY },
    { label: t('quarterly'), value: EScheduleFrequency.QUARTERLY },
    { label: t('semiannually'), value: EScheduleFrequency.SEMIANNUALLY },
    { label: t('annually'), value: EScheduleFrequency.ANNUALLY }
];

export const getBillingStatusList = (t: (val: string) => string) => [
    {
        label: t('active'),
        value: EScheduleStatus.ACTIVE
    },
    {
        label: t('paused'),
        value: EScheduleStatus.INACTIVE
    },
    {
        label: 'Error',
        value: EScheduleStatus.ERROR
    },
    {
        label: t('closed'),
        value: EScheduleStatus.COMPLETE
    }
];

export const getGiftCardStatusesList = [
    {
        label: 'Issued',
        value: EGiftCardStatus.ISSUED
    },
    {
        label: 'Active',
        value: EGiftCardStatus.ACTIVE
    },
    {
        label: 'Partially redeemed',
        value: EGiftCardStatus.PARTIALLY_REDEEMED
    },
    {
        label: 'Redeemed',
        value: EGiftCardStatus.REDEEMED
    },
    {
        label: 'Cancelled',
        value: EGiftCardStatus.CANCELLED
    },
    {
        label: 'Cancel requested',
        value: EGiftCardStatus.CANCEL_REQUESTED
    },
    {
        label: 'Pending',
        value: EGiftCardStatus.PENDING
    }
];

export function getNextPaymentDate({
    frequency,
    nextPaymentAt,
    lastPaymentAt
}: {
    frequency: EScheduleFrequency;
    nextPaymentAt?: string | Date;
    lastPaymentAt?: string | Date;
}): string {
    // If nextPaymentAt exists, return it directly
    if (nextPaymentAt) {
        const nextDate = new Date(nextPaymentAt);
        if (isNaN(nextDate.getTime())) {
            throw new Error('Invalid nextPaymentAt provided');
        }
        return nextDate.toISOString();
    }

    // If only lastPaymentAt exists, calculate the next payment from it
    if (lastPaymentAt) {
        const lastDate = new Date(lastPaymentAt);

        // Validate the input date
        if (isNaN(lastDate.getTime())) {
            throw new Error('Invalid lastPaymentAt provided');
        }

        // Create a new date object to avoid mutating the original
        const nextDate = new Date(lastDate);

        switch (frequency) {
            case EScheduleFrequency.ONCE:
                // If it's a one-time payment, return the last payment date
                return nextDate.toISOString();

            case EScheduleFrequency.DAILY:
                nextDate.setDate(nextDate.getDate() + 1);
                return nextDate.toISOString();

            case EScheduleFrequency.WEEKLY:
                nextDate.setDate(nextDate.getDate() + 7);
                return nextDate.toISOString();

            case EScheduleFrequency.BIWEEKLY:
                nextDate.setDate(nextDate.getDate() + 14);
                return nextDate.toISOString();

            case EScheduleFrequency.MONTHLY:
                nextDate.setMonth(nextDate.getMonth() + 1);
                return nextDate.toISOString();

            case EScheduleFrequency.QUARTERLY:
                nextDate.setMonth(nextDate.getMonth() + 3);
                return nextDate.toISOString();

            case EScheduleFrequency.SEMIANNUALLY:
                nextDate.setMonth(nextDate.getMonth() + 6);
                return nextDate.toISOString();

            case EScheduleFrequency.ANNUALLY:
                nextDate.setFullYear(nextDate.getFullYear() + 1);
                return nextDate.toISOString();

            default:
                throw new Error('Invalid payment frequency');
        }
    }

    // If neither date is provided, throw an error
    throw new Error('Either nextPaymentAt or lastPaymentAt must be provided');
}
